import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Star, LogIn, Crown, Sparkles, AlertTriangle } from "lucide-react";
import { getFavourites } from "../lib/favourites";
import { FoinnLogo } from "./FoinnLogo";
import { useAuth } from "../contexts/AuthContext";

export const AppHeader = ({ showFavourites = true }) => {
  const navigate = useNavigate();
  const { user, login, openBilling } = useAuth();
  const [favs, setFavs] = useState([]);

  useEffect(() => {
    const update = () => setFavs(getFavourites());
    update();
    window.addEventListener("fav-changed", update);
    return () => window.removeEventListener("fav-changed", update);
  }, []);

  const notice = user?.renewal_notice;
  const premium = user && user.plan !== "free";

  return (
    <>
      <header
        className="mx-auto flex max-w-6xl items-center justify-between gap-2 px-5 pt-6"
        data-testid="app-header"
      >
        <button onClick={() => navigate("/")} className="flex items-center gap-2" data-testid="header-logo">
          <FoinnLogo className="h-12 w-12" />
          <div className="text-left">
            <h1 className="font-serif text-3xl font-bold leading-none text-[#2d2824]">Foinn.org</h1>
            <p className="text-[11px] font-bold uppercase tracking-widest text-[#a89e92]">
              Folk Tune Finder
            </p>
          </div>
        </button>

        <div className="flex items-center gap-2">
          {showFavourites && (
            <button
              onClick={() => navigate("/favourites")}
              className="flex items-center gap-2 rounded-full border border-[#e5e0d8] bg-white px-3 py-2 text-sm font-bold text-[#2d2824] transition-colors hover:bg-[#f3efea]"
              data-testid="favourites-button"
            >
              <Star className="h-4 w-4" style={{ color: "#4CAF50", fill: "#4CAF50" }} />
              <span className="hidden sm:inline">Favourites</span>
              {favs.length > 0 && (
                <span className="rounded-full bg-[#4CAF50] px-1.5 text-xs text-white">
                  {favs.length}
                </span>
              )}
            </button>
          )}

          {user ? (
            <button
              onClick={() => (user.is_admin ? navigate("/admin") : openBilling())}
              className="relative flex items-center gap-2 rounded-full border border-[#e5e0d8] bg-white py-1 pl-1 pr-3 text-sm font-bold text-[#2d2824] transition-colors hover:bg-[#f3efea]"
              data-testid="account-button"
              title={user.is_admin ? "Admin dashboard" : "Your plan & AI analyses"}
            >
              {user.picture ? (
                <img src={user.picture} alt="" className="h-8 w-8 rounded-full object-cover" />
              ) : (
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#2d2824] text-xs text-white">
                  {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                </span>
              )}
              <span className="flex items-center gap-1 tabular-nums" data-testid="calls-pill">
                <Sparkles className="h-3.5 w-3.5 text-[#8a2be2]" />
                {user.calls_left}
              </span>
              {premium && (
                <span
                  className="absolute -right-1 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-[#8a2be2]"
                  title={user.plan_label}
                  data-testid="premium-badge"
                >
                  <Crown className="h-2.5 w-2.5 text-white" />
                </span>
              )}
            </button>
          ) : (
            <button
              onClick={login}
              className="flex items-center gap-2 rounded-full bg-[#2d2824] px-4 py-2 text-sm font-bold text-white transition-transform hover:scale-105"
              data-testid="signin-button"
            >
              <LogIn className="h-4 w-4" /> <span className="hidden sm:inline">Sign in</span>
            </button>
          )}
        </div>
      </header>

      {notice && (
        <div className="mx-auto mt-3 max-w-6xl px-5">
          <button
            onClick={openBilling}
            className="flex w-full items-center gap-2 rounded-2xl border-2 border-[#ff6b00]/40 bg-[#fff4ea] px-4 py-2.5 text-left text-sm font-bold text-[#8a4300]"
            data-testid="renewal-alert"
          >
            <AlertTriangle className="h-4 w-4 shrink-0" />
            {notice.cancelled
              ? `Your Premium ends in ${notice.days_left} day${notice.days_left === 1 ? "" : "s"} (${new Date(notice.expires_at).toLocaleDateString()}) — renew to keep unlimited searching.`
              : `Your plan renews in ${notice.days_left} day${notice.days_left === 1 ? "" : "s"} (${new Date(notice.expires_at).toLocaleDateString()}).`}
          </button>
        </div>
      )}
    </>
  );
};
